import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Unpersisted } from "types/utility";

import {
  useCreateBatch,
  useDeleteBatch,
  useSendBatchForReview,
} from "hooks/api/use-batches";

import { Route as AppRoute } from "utilities/app-routes";
import { toApiDate } from "utilities/date";

import { BatchItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";

interface UseBatchActionsProps {
  clientId: string | undefined;
  setIsCreateBatchModalOpen?: (isOpen: boolean) => void;
}

const useBatchActions = ({
  clientId,
  setIsCreateBatchModalOpen,
}: UseBatchActionsProps) => {
  const navigate = useNavigate();
  const createBatchMutation = useCreateBatch({ clientId });
  const sendBatchForReviewMutation = useSendBatchForReview({ clientId });
  const deleteBatchMutation = useDeleteBatch({ clientId });

  const handleCreateBatch = async (values: Unpersisted<BatchItem>) => {
    const batch = await createBatchMutation.mutateAsync({
      ...values,
      dueDate: toApiDate(values.dueDate),
    });

    navigate(`${location.pathname}/${AppRoute.batch}/${batch.id}`);
    setIsCreateBatchModalOpen?.(false);
  };

  const handleSendForReview = useCallback(
    async (id: string) => {
      await sendBatchForReviewMutation.mutateAsync({ batchId: id });
    },
    [sendBatchForReviewMutation],
  );

  const handleDeleteBatch = useCallback(
    async (id: string) => {
      await deleteBatchMutation.mutateAsync({ batchId: id });
    },
    [deleteBatchMutation],
  );

  return {
    handleCreateBatch,
    handleSendForReview,
    handleDeleteBatch,
    isBatchActionPending:
      createBatchMutation.isPending ||
      sendBatchForReviewMutation.isPending ||
      deleteBatchMutation.isPending,
  };
};

export default useBatchActions;
